import { createContext, useContext } from "react";
import type { AgeGroup } from "../design-tokens";
import { touchTargets } from "../design-tokens";

/**
 * Contesto delle primitive TL4K.
 *
 * Due context distinti, di proposito: densità e touch target sono assi
 * ortogonali (piano §1) e hanno provider diversi.
 *
 *  - AgeGroupContext  → lo fornisce ThemeProvider (popolazione utente)
 *  - DensityContext   → lo fornisce DensityProvider / CompactAppLayout (layout)
 *
 * Collassarli in un unico context riporterebbe al bug d'origine: "sono in
 * embedded, quindi sono compatto, quindi non ho bisogno di touch target" è
 * un non-sequitur — una app studente embedded ha bisogno di entrambi.
 */

// ─── Age group (popolazione utente) ───────────────────────────

export const AgeGroupContext = createContext<AgeGroup | null>(null);

/** Fascia d'età dichiarata dal ThemeProvider più vicino, se presente. */
export function useAgeGroup(): AgeGroup | null {
  return useContext(AgeGroupContext);
}

// ─── Densità (contesto di layout) ─────────────────────────────

export type Density = "compact" | "comfortable";

export const DensityContext = createContext<Density | null>(null);

export interface DensityProviderProps {
  value: Density;
  children: React.ReactNode;
}

/**
 * Imposta la densità di default per tutte le primitive nel sottoalbero.
 * Tipicamente `compact` dentro la shell embedded di TechyWeb (budget 688px).
 */
export function DensityProvider({ value, children }: DensityProviderProps) {
  return <DensityContext.Provider value={value}>{children}</DensityContext.Provider>;
}

/** Densità ereditata; `comfortable` se nessun provider la dichiara. */
export function useDensity(explicit?: Density): Density {
  const inherited = useContext(DensityContext);
  return explicit ?? inherited ?? "comfortable";
}

// ─── Touch target ─────────────────────────────────────────────

/**
 * Specifica del touch target di una primitiva.
 *
 *  - `false`      → nessun minimo imposto (DEFAULT). Superfici insegnante/admin
 *                   su desktop: resta comunque conforme a WCAG 2.2 AA SC 2.5.8
 *                   (24px) perché la densità più bassa è 34px.
 *  - `true`       → eredita la fascia d'età dal ThemeProvider.
 *  - `"4-6" | …`  → forza esplicitamente una fascia (60 / 48 / 44px).
 *
 * Il default è `false` DI PROPOSITO: un minimo implicito ovunque è la
 * regressione che ha originato questo lavoro. Le superfici student-facing
 * devono dichiararlo — un'omissione visibile in review è preferibile a un
 * 44px silenzioso sulle dashboard insegnante.
 */
export type TouchSpec = boolean | AgeGroup;

/**
 * Risolve il touch target in un valore CSS.
 * Ritorna `undefined` quando non va imposto alcun minimo.
 */
export function resolveTouchMin(
  touch: TouchSpec | undefined,
  inheritedAgeGroup: AgeGroup | null,
): string | undefined {
  if (!touch) return undefined;

  if (touch === true) {
    if (!inheritedAgeGroup) {
      if (import.meta.env.DEV) {
        console.warn(
          "[TL4K] touch={true} richiede un <ThemeProvider ageGroup=...> a monte. " +
            "Nessun minimo applicato: dichiara ageGroup, oppure passa touch=\"11-14\" esplicito.",
        );
      }
      return undefined;
    }
    return touchTargets[inheritedAgeGroup].minSize;
  }

  return touchTargets[touch].minSize;
}
